import type { MouseEventHandler, MutableRefObject, ReactNode } from "react";
import { useRef, useCallback } from "react";
import cx from "ui/cx";
import styles from "./SplitFlex.module.scss";

export interface SplitFlexProps extends React.HTMLAttributes<HTMLDivElement> {
  children: [firstChild: ReactNode, lastChild: ReactNode];
  direction?: "vertical" | "horizontal";
  sizeRatio?: number; // ratio of first/last between 0 and 1
  firstMinSize?: number; // pixels
  lastMinSize?: number; // pixels
  canResize?: boolean;
  responsive?: boolean;
  hideSide?: "first" | "last";
  firstRefContent?: MutableRefObject<HTMLDivElement>;
  lastRefContent?: MutableRefObject<HTMLDivElement | null>;
}

// A responsive, flex based implementation of SplitGroup
// if horizontal, switches to vertical when (orientation: portrait). Expand behavior as needed
export default function SplitFlex({
  children: [firstChild, lastChild],
  direction = "horizontal",
  responsive = true,
  canResize = true,
  sizeRatio = 0.5,
  hideSide,
  firstMinSize = 150,
  lastMinSize = 150,
  className,
  firstRefContent,
  lastRefContent,
  ...props
}: SplitFlexProps) {
  const separatorRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLDivElement>(null);
  const lastRef = useRef<HTMLDivElement>(null);

  const resize: MouseEventHandler<HTMLDivElement> = useCallback(
    (startPos) => {
      const firstEle = firstRef.current;
      const lastEle = lastRef.current;

      if (firstEle && lastEle) {
        const firstBox = firstEle.getBoundingClientRect();
        const lastBox = lastEle.getBoundingClientRect();
        // Get the direction of panels (may be responsively updated)
        const dir = firstBox.x === lastBox.x ? "y" : "x";

        // Get the current ratio
        const flexBasis = parseFloat(firstEle.style.flexBasis);
        const currentRatio = flexBasis / (flexBasis + 100);

        // Add a mousemove function to recalulate flexBasis
        document.onmousemove = (e: MouseEvent) =>
          window.requestAnimationFrame(() => {
            if (!firstEle) {
              return;
            }
            const delta = dir === "x" ? e.clientX - startPos.clientX : e.clientY - startPos.clientY;
            const firstSize = dir === "x" ? firstBox.width : firstBox.height;
            const lastSize = dir === "x" ? lastBox.width : lastBox.height;
            const ratio = delta / (firstSize + lastSize);

            // check min sizes before applying
            const newFirstSize = (currentRatio + ratio) * (firstBox.width + lastBox.width);
            if (firstMinSize >= newFirstSize) {
              return;
            }
            if (lastMinSize >= firstBox.width + lastBox.width - newFirstSize) {
              return;
            }
            // apply new flex basis
            firstEle.style.flexBasis = calcFlexBasis(currentRatio + ratio);
          });

        document.onmouseup = () => {
          document.onmousemove = null;
          document.onmouseup = null;
        };
      }
    },
    [firstMinSize, lastMinSize],
  );

  return (
    <div
      className={cx(
        styles.wrapper,
        styles[direction],
        hideSide && styles[`hide-${hideSide}`],
        responsive && direction === "horizontal" && styles.responsive,
        className,
      )}
      {...props}
    >
      <div className={styles.first} ref={firstRef} style={{ flexBasis: calcFlexBasis(sizeRatio) }}>
        <div className={cx(styles.content, "SplitFlex-firstContent")} ref={firstRefContent}>
          {firstChild}
        </div>
      </div>
      {canResize && !hideSide && <div className={styles.bar} ref={separatorRef} onMouseDown={resize} />}

      <div className={styles.last} ref={lastRef}>
        <div className={cx(styles.content, "SplitFlex-lastContent")} ref={lastRefContent}>
          {lastChild}
        </div>
      </div>
    </div>
  );
}

// calculates flex basis for the size ratio relative to 100%.
const calcFlexBasis = (ratio: number) => `${ratio !== 1 ? (ratio / (1 - ratio)) * 100 : 100}%`;
